import React, { useState, useMemo, useRef, useCallback } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuCheckboxItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '@/components/ui/dropdown-menu'
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger,
} from '@/components/ui/context-menu'
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd'
import { 
  Filter, 
  Plus, 
  Settings, 
  Eye,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  EyeOff,
  List,
  GripVertical,
  ArrowRight,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  X
} from 'lucide-react'
import { TablePagination } from '@/components/ui/table-pagination'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Label } from '@/components/ui/label'
import { ScrollArea } from '@/components/ui/scroll-area'

export interface DataTableColumn<T = any> {
  id: string
  label: string
  visible: boolean
  sortable?: boolean
  width?: number
  order: number
  icon?: React.ReactNode
}

export interface DataTableView {
  id: string
  name: string
  type: 'table' | 'kanban'
  filters?: any
  columns?: DataTableColumn[]
}

export interface DataTableProps<T = any> {
  // Data
  data: T[]
  filteredData: T[]
  totalItems: number
  
  // Columns configuration
  columns: DataTableColumn<T>[]
  defaultColumns?: DataTableColumn<T>[]
  onColumnsChange?: (columns: DataTableColumn<T>[]) => void
  
  // Cell rendering
  renderCell: (item: T, columnId: string) => React.ReactNode
  
  // Actions
  onRowClick?: (item: T) => void
  renderActions?: (item: T) => React.ReactNode
  
  // Selection
  selectedIds?: string[]
  onSelectChange?: (ids: string[]) => void
  getItemId: (item: T) => string
  
  // Sorting
  sortField?: string
  sortDirection?: 'asc' | 'desc'
  onSortChange?: (field: string, direction: 'asc' | 'desc') => void
  
  // Pagination
  currentPage?: number
  totalPages?: number
  itemsPerPage?: number
  onPageChange?: (page: number) => void
  onItemsPerPageChange?: (itemsPerPage: number) => void
  
  // Loading states
  isLoading?: boolean
  isFetching?: boolean
  
  // Views
  views?: DataTableView[]
  currentView?: string
  onViewChange?: (viewId: string) => void
  onCreateView?: () => void
  
  // Filters (custom filter UI)
  renderFilters?: () => React.ReactNode
  showFilterDialog?: boolean
  onFilterDialogChange?: (open: boolean) => void
  
  // Empty state
  emptyStateTitle?: string
  emptyStateDescription?: string
  emptyStateIcon?: React.ReactNode
  
  // Header actions
  headerActions?: React.ReactNode
  
  // Styling
  className?: string
  checkboxColumnWidth?: number
  actionsColumnWidth?: number
  enableTextWrapping?: boolean // Enable text wrapping instead of truncation
}

export function DataTable<T = any>({
  data,
  filteredData,
  totalItems,
  columns: initialColumns,
  defaultColumns = [],
  onColumnsChange,
  renderCell,
  onRowClick,
  renderActions,
  selectedIds = [],
  onSelectChange,
  getItemId,
  sortField,
  sortDirection = 'desc',
  onSortChange,
  currentPage = 1,
  totalPages = 1,
  itemsPerPage = 15,
  onPageChange,
  onItemsPerPageChange,
  isLoading = false,
  isFetching = false,
  views = [],
  currentView,
  onViewChange,
  onCreateView,
  renderFilters,
  showFilterDialog = false,
  onFilterDialogChange,
  emptyStateTitle = 'No items found',
  emptyStateDescription = 'Try adjusting your filters',
  emptyStateIcon,
  headerActions,
  className = '',
  checkboxColumnWidth = 96,
  actionsColumnWidth = 48,
  enableTextWrapping = false
}: DataTableProps<T>) {
  const [columns, setColumns] = useState<DataTableColumn<T>[]>(initialColumns || defaultColumns)
  const [internalSortField, setInternalSortField] = useState<string>('')
  const [internalSortDirection, setInternalSortDirection] = useState<'asc' | 'desc'>('desc')
  const [resizingColumn, setResizingColumn] = useState<string | null>(null)
  const [resizeStartX, setResizeStartX] = useState(0)
  const [resizeStartWidth, setResizeStartWidth] = useState(0)
  const [showViewSidebar, setShowViewSidebar] = useState(false)
  const [showFieldsSidebar, setShowFieldsSidebar] = useState(false)
  const tableRef = useRef<HTMLDivElement>(null)

  // Use internal or external sort state
  const activeSortField = sortField !== undefined ? sortField : internalSortField
  const activeSortDirection = sortDirection !== undefined ? sortDirection : internalSortDirection

  // Sync columns with parent if callback provided
  React.useEffect(() => {
    if (onColumnsChange) {
      onColumnsChange(columns)
    }
  }, [columns, onColumnsChange])

  // Handle selection — only select items on the current page
  const handleSelectAll = (checked: boolean) => {
    if (onSelectChange) {
      if (checked) {
        onSelectChange(displayData.map(getItemId))
      } else {
        onSelectChange([])
      }
    }
  }

  const handleSelectItem = (itemId: string, checked: boolean) => {
    if (onSelectChange) {
      if (checked) {
        onSelectChange([...selectedIds, itemId])
      } else {
        onSelectChange(selectedIds.filter(id => id !== itemId))
      }
    }
  }

  // Sort data
  const sortedData = useMemo(() => {
    if (!activeSortField) return filteredData

    const sorted = [...filteredData]
    sorted.sort((a, b) => {
      const aValue = (a as any)[activeSortField]
      const bValue = (b as any)[activeSortField]

      if (aValue === bValue) return 0
      if (aValue === null || aValue === undefined) return 1
      if (bValue === null || bValue === undefined) return -1

      // Handle dates
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const aDate = new Date(aValue).getTime()
        const bDate = new Date(bValue).getTime()
        if (!isNaN(aDate) && !isNaN(bDate)) {
          return activeSortDirection === 'asc' ? aDate - bDate : bDate - aDate
        }
      }

      // Handle strings
      const aStr = String(aValue).toLowerCase()
      const bStr = String(bValue).toLowerCase()
      const comparison = aStr < bStr ? -1 : 1
      return activeSortDirection === 'asc' ? comparison : -comparison
    })

    return sorted
  }, [filteredData, activeSortField, activeSortDirection])

  // Paginate sorted data
  const displayTotalPages = totalPages || Math.ceil(sortedData.length / (itemsPerPage || 15))
  const displayData = useMemo(() => {
    if (!onPageChange) return sortedData
    const startIndex = ((currentPage || 1) - 1) * (itemsPerPage || 15)
    const endIndex = startIndex + (itemsPerPage || 15)
    return sortedData.slice(startIndex, endIndex)
  }, [sortedData, currentPage, itemsPerPage, onPageChange])

  // Get visible columns, sorted by order
  const displayColumns = useMemo(() => {
    return columns.filter(col => col.visible).sort((a, b) => a.order - b.order)
  }, [columns])

  // Calculate total width for proportional sizing when wrapping is enabled
  // Note: When enableTextWrapping=true, width values should be percentages (e.g., 15 = 15%)
  // When enableTextWrapping=false, width values should be pixels (e.g., 200 = 200px)
  const totalColumnWidth = useMemo(() => {
    return displayColumns.reduce((sum, col) => sum + (col.width || 200), 0)
  }, [displayColumns])

  // Column resizing handlers
  const handleResizeStart = useCallback((columnId: string, e: React.MouseEvent) => {
    setResizingColumn(columnId)
    setResizeStartX(e.clientX)
    const column = columns.find(col => col.id === columnId)
    if (column && tableRef.current) {
      const th = tableRef.current.querySelector(`th[data-column-id="${columnId}"]`)
      if (th) {
        setResizeStartWidth(th.clientWidth)
      }
    }
  }, [columns])

  const handleResize = useCallback((e: MouseEvent) => {
    if (resizingColumn) {
      const deltaX = e.clientX - resizeStartX
      setColumns(prev => prev.map(col => {
        if (col.id === resizingColumn) {
          const newWidth = Math.max(100, resizeStartWidth + deltaX)
          return { ...col, width: newWidth }
        }
        return col
      }))
    }
  }, [resizingColumn, resizeStartX, resizeStartWidth])

  const handleResizeEnd = useCallback(() => {
    setResizingColumn(null)
  }, [])

  React.useEffect(() => {
    if (resizingColumn) {
      document.addEventListener('mousemove', handleResize)
      document.addEventListener('mouseup', handleResizeEnd)
      return () => {
        document.removeEventListener('mousemove', handleResize)
        document.removeEventListener('mouseup', handleResizeEnd)
      }
    }
  }, [resizingColumn, handleResize, handleResizeEnd])

  // Column handlers
  const handleColumnSort = (columnId: string, direction: 'asc' | 'desc') => {
    if (onSortChange) {
      onSortChange(columnId, direction)
    } else {
      setInternalSortField(columnId)
      setInternalSortDirection(direction)
    }
  }

  const handleColumnMoveRight = (columnId: string) => {
    setColumns(prev => {
      const updated = [...prev].sort((a, b) => a.order - b.order)
      const index = updated.findIndex(c => c.id === columnId)
      if (index !== -1 && index < updated.length - 1) {
        const currentOrder = updated[index].order
        updated[index].order = updated[index + 1].order
        updated[index + 1].order = currentOrder
      }
      return updated.sort((a, b) => a.order - b.order)
    })
  }

  const handleColumnHide = (columnId: string) => {
    setColumns(prev => prev.map(col => 
      col.id === columnId ? { ...col, visible: false } : col
    ))
  }

  // Handle drag and drop for columns
  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return

    const sourceIndex = result.source.index
    const destinationIndex = result.destination.index

    if (sourceIndex === destinationIndex && result.source.droppableId === result.destination.droppableId) return

    setColumns(prev => {
      const updated = [...prev]
      const visible = updated.filter(col => col.visible).sort((a, b) => a.order - b.order)
      const hidden = updated.filter(col => !col.visible).sort((a, b) => a.order - b.order)

      if (result.source.droppableId === 'visible-fields' && result.destination?.droppableId === 'visible-fields') {
        const [removed] = visible.splice(sourceIndex, 1)
        visible.splice(destinationIndex, 0, removed)
        visible.forEach((col, idx) => {
          const colIndex = updated.findIndex(c => c.id === col.id)
          if (colIndex !== -1) {
            updated[colIndex].order = idx
          }
        })
        return updated
      } else if (result.source.droppableId === 'visible-fields' && result.destination?.droppableId === 'hidden-fields') {
        const [removed] = visible.splice(sourceIndex, 1)
        const colIndex = updated.findIndex(c => c.id === removed.id)
        if (colIndex !== -1) {
          updated[colIndex].visible = false
          updated[colIndex].order = visible.length + hidden.length
        }
        visible.forEach((col, idx) => {
          const cIndex = updated.findIndex(c => c.id === col.id)
          if (cIndex !== -1) updated[cIndex].order = idx
        })
        return updated
      } else if (result.source.droppableId === 'hidden-fields' && result.destination?.droppableId === 'visible-fields') {
        const [removed] = hidden.splice(sourceIndex, 1)
        const colIndex = updated.findIndex(c => c.id === removed.id)
        if (colIndex !== -1) {
          updated[colIndex].visible = true
          updated[colIndex].order = destinationIndex
        }
        visible.splice(destinationIndex, 0, removed)
        visible.forEach((col, idx) => {
          const cIndex = updated.findIndex(c => c.id === col.id)
          if (cIndex !== -1) updated[cIndex].order = idx
        })
        return updated
      } else if (result.source.droppableId === 'hidden-fields' && result.destination?.droppableId === 'hidden-fields') {
        const [removed] = hidden.splice(sourceIndex, 1)
        hidden.splice(destinationIndex, 0, removed)
        visible.forEach((col, idx) => {
          const colIndex = updated.findIndex(c => c.id === col.id)
          if (colIndex !== -1) {
            updated[colIndex].order = idx
          }
        })
        hidden.forEach((col, idx) => {
          const colIndex = updated.findIndex(c => c.id === col.id)
          if (colIndex !== -1) {
            updated[colIndex].order = visible.length + idx
          }
        })
        return updated
      }

      return prev
    })
  }

  const handleToggleColumnVisibility = (columnId: string) => {
    setColumns(prev => {
      const updated = prev.map(col => 
        col.id === columnId ? { ...col, visible: !col.visible } : col
      )
      const visible = updated.filter(col => col.visible).sort((a, b) => a.order - b.order)
      const hidden = updated.filter(col => !col.visible).sort((a, b) => a.order - b.order)
      
      visible.forEach((col, idx) => {
        const colIndex = updated.findIndex(c => c.id === col.id)
        if (colIndex !== -1) updated[colIndex].order = idx
      })
      hidden.forEach((col, idx) => {
        const colIndex = updated.findIndex(c => c.id === col.id)
        if (colIndex !== -1) updated[colIndex].order = visible.length + idx
      })
      return updated
    })
  }

  const handleAutoFitColumns = () => {
    setColumns(prev => prev.map(col => {
      const baseWidth = col.label.length * 8 + 60
      const minWidth = Math.max(100, baseWidth)
      return { ...col, width: minWidth }
    }))
  }

  const allSelected = data.length > 0 && selectedIds.length === data.length
  const currentViewName = views.find(v => v.id === currentView)?.name || 'All Items'

  return (
    <div className={className}>
      {/* Table with integrated header - no Card wrapper to blend with parent */}
      <div>
        {/* Header integrated */}
        <div className="flex items-center justify-between px-6 py-3 border-b">
        <div className="flex items-center gap-2">
          {views.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-2">
                  <List className="h-4 w-4" />
                  {currentViewName} • {totalItems}
                  {isFetching && (
                    <RefreshCw className="h-3 w-3 animate-spin text-muted-foreground" />
                  )}
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                {views.map(view => (
                  <DropdownMenuItem
                    key={view.id}
                    onClick={() => onViewChange?.(view.id)}
                    className={currentView === view.id ? 'bg-muted' : ''}
                  >
                    <div className="flex items-center gap-2 w-full">
                      <List className="h-4 w-4" />
                      {view.name}
                    </div>
                  </DropdownMenuItem>
                ))}
                {onCreateView && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => setShowViewSidebar(true)}>
                      <Plus className="h-4 w-4 mr-2" />
                      Create view
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
        <div className="flex items-center gap-2">
          {renderFilters && (
            <Button 
              variant={showFilterDialog ? "secondary" : "outline"} 
              size="sm"
              onClick={() => onFilterDialogChange?.(!showFilterDialog)}
            >
              <Filter className="h-4 w-4 mr-2" />
              Filter
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <ArrowUpDown className="h-4 w-4 mr-2" />
                Sort
                <ChevronDown className="h-4 w-4 ml-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {displayColumns.filter(col => col.sortable).map(col => (
                <DropdownMenuItem
                  key={col.id}
                  onClick={() => {
                    if (activeSortField === col.id) {
                      const newDirection = activeSortDirection === 'asc' ? 'desc' : 'asc'
                      handleColumnSort(col.id, newDirection)
                    } else {
                      handleColumnSort(col.id, 'asc')
                    }
                  }}
                >
                  <div className="flex items-center gap-2">
                    {col.label}
                    {activeSortField === col.id && (
                      activeSortDirection === 'asc' ? (
                        <ArrowUp className="h-3 w-3" />
                      ) : (
                        <ArrowDown className="h-3 w-3" />
                      )
                    )}
                  </div>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <ChevronDown className="h-4 w-4" />
                Options
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setShowFieldsSidebar(true)}>
                <Settings className="h-4 w-4 mr-2" />
                Fields
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleAutoFitColumns}>
                <ArrowUpDown className="h-4 w-4 mr-2" />
                Auto-fit columns
              </DropdownMenuItem>
              {defaultColumns.length > 0 && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setColumns(defaultColumns)}>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Reset Columns
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {defaultColumns.map(col => (
                    <DropdownMenuCheckboxItem
                      key={col.id}
                      checked={columns.find(c => c.id === col.id)?.visible ?? false}
                      onCheckedChange={(checked) => {
                        setColumns(prev =>
                          prev.map(c => c.id === col.id ? { ...c, visible: checked } : c)
                        )
                      }}
                    >
                      {col.label}
                    </DropdownMenuCheckboxItem>
                  ))}
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          {headerActions}
        </div>
      </div>

      {/* Table */}
        <div className="p-0">
          <div className="border rounded-lg overflow-hidden" ref={tableRef}>
            <div className={enableTextWrapping ? "overflow-x-auto w-full" : "overflow-x-auto"}>
              <Table className={enableTextWrapping ? "table-fixed w-full" : "w-full"} style={{ minWidth: '100%' }}>
                <TableHeader>
                  <TableRow>
                    {onSelectChange && (
                      <TableHead 
                        className="sticky left-0 bg-background z-10" 
                        style={{ 
                          width: `${checkboxColumnWidth}px`,
                          paddingLeft: '16px', 
                          paddingRight: '8px', 
                          paddingTop: '12px', 
                          paddingBottom: '12px' 
                        }}
                      >
                        <Checkbox
                          checked={allSelected}
                          onCheckedChange={handleSelectAll}
                        />
                      </TableHead>
                    )}
                    {displayColumns.map(column => {
                      const columnWidth = column.width || 200
                      const isSorted = activeSortField === column.id
                      return (
                        <ContextMenu key={column.id}>
                          <ContextMenuTrigger asChild>
                            <TableHead
                              data-column-id={column.id}
                              style={enableTextWrapping ? { 
                                width: `${columnWidth}%`
                              } : { width: `${columnWidth}px`, minWidth: `${columnWidth}px` }}
                              className={`relative border-r border-border/40 px-4 py-3 ${enableTextWrapping ? '' : ''} ${column.sortable ? 'cursor-pointer hover:bg-muted/50' : ''}`}
                              onClick={(e) => {
                                if ((e.target as HTMLElement).closest('.cursor-col-resize')) {
                                  return
                                }
                                if (column.sortable) {
                                  if (activeSortField === column.id) {
                                    const newDirection = activeSortDirection === 'asc' ? 'desc' : 'asc'
                                    handleColumnSort(column.id, newDirection)
                                  } else {
                                    handleColumnSort(column.id, 'asc')
                                  }
                                }
                              }}
                            >
                              <div className="flex items-center justify-between gap-2 pr-4">
                                <div className={`flex items-center gap-2 ${enableTextWrapping ? 'break-words whitespace-normal' : 'truncate'}`}>
                                  {column.icon}
                                  {column.label}
                                  {isSorted && (
                                    activeSortDirection === 'asc' ? (
                                      <ArrowUp className="h-3 w-3 flex-shrink-0" />
                                    ) : (
                                      <ArrowDown className="h-3 w-3 flex-shrink-0" />
                                    )
                                  )}
                                </div>
                              </div>
                              <div
                                className={`absolute right-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-primary/40 transition-colors flex items-center justify-center ${
                                  resizingColumn === column.id ? 'bg-primary/50' : ''
                                }`}
                                onMouseDown={(e) => handleResizeStart(column.id, e)}
                                title="Drag to resize column"
                              >
                                <div className="w-px h-full bg-border/50" />
                              </div>
                            </TableHead>
                          </ContextMenuTrigger>
                          <ContextMenuContent>
                            {column.sortable && (
                              <>
                                <ContextMenuSub>
                                  <ContextMenuSubTrigger>
                                    <ArrowUpDown className="h-4 w-4 mr-2" />
                                    Sort
                                  </ContextMenuSubTrigger>
                                  <ContextMenuSubContent>
                                    <ContextMenuItem onClick={() => handleColumnSort(column.id, 'asc')}>
                                      <ArrowUp className="h-4 w-4 mr-2" />
                                      Sort ascending
                                    </ContextMenuItem>
                                    <ContextMenuItem onClick={() => handleColumnSort(column.id, 'desc')}>
                                      <ArrowDown className="h-4 w-4 mr-2" />
                                      Sort descending
                                    </ContextMenuItem>
                                  </ContextMenuSubContent>
                                </ContextMenuSub>
                                <ContextMenuSeparator />
                              </>
                            )}
                            <ContextMenuItem onClick={() => handleColumnMoveRight(column.id)}>
                              <ArrowRight className="h-4 w-4 mr-2" />
                              Move right
                            </ContextMenuItem>
                            <ContextMenuSeparator />
                            <ContextMenuItem onClick={() => handleColumnHide(column.id)}>
                              <EyeOff className="h-4 w-4 mr-2" />
                              Hide
                            </ContextMenuItem>
                          </ContextMenuContent>
                        </ContextMenu>
                      )
                    })}
                    {renderActions && (
                      <TableHead className="sticky right-0 bg-background z-10 px-4 py-3" style={{ width: `${actionsColumnWidth}px` }}>
                        Actions
                      </TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={displayColumns.length + (onSelectChange ? 1 : 0) + (renderActions ? 1 : 0)} className="text-center py-12">
                        <RefreshCw className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
                      </TableCell>
                    </TableRow>
                  ) : displayData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={displayColumns.length + (onSelectChange ? 1 : 0) + (renderActions ? 1 : 0)} className="text-center py-12">
                        {emptyStateIcon || <List className="h-12 w-12 text-muted-foreground mx-auto mb-4" />}
                        <h3 className="text-lg font-semibold mb-2">{emptyStateTitle}</h3>
                        <p className="text-muted-foreground">{emptyStateDescription}</p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    displayData.map((item) => {
                      const itemId = getItemId(item)
                      return (
                        <TableRow
                          key={itemId}
                          className={onRowClick ? "cursor-pointer hover:bg-muted/50" : ""}
                          onClick={() => onRowClick?.(item)}
                        >
                          {onSelectChange && (
                            <TableCell 
                              className="sticky left-0 bg-background z-10" 
                              style={{ 
                                paddingLeft: '16px', 
                                paddingRight: '8px', 
                                paddingTop: '12px', 
                                paddingBottom: '12px' 
                              }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="flex items-center justify-start w-full">
                                <Checkbox
                                  checked={selectedIds.includes(itemId)}
                                  onCheckedChange={(checked) => handleSelectItem(itemId, checked)}
                                  className="flex-shrink-0"
                                />
                              </div>
                            </TableCell>
                          )}
                          {displayColumns.map(column => {
                            const cellColumnWidth = column.width || 200
                            return (
                            <TableCell 
                              key={column.id}
                              style={enableTextWrapping ? {
                                width: `${cellColumnWidth}%`
                              } : {
                                width: `${cellColumnWidth}px`, 
                                minWidth: `${cellColumnWidth}px`, 
                                maxWidth: `${cellColumnWidth}px` 
                              }}
                              className={`border-r border-border/30 px-4 py-3 ${enableTextWrapping ? 'break-words whitespace-normal' : 'overflow-hidden'}`}
                            >
                              <div className={enableTextWrapping ? "break-words whitespace-normal" : "truncate"}>
                                {renderCell(item, column.id)}
                              </div>
                            </TableCell>
                          )
                          })}
                          {renderActions && (
                            <TableCell 
                              className="sticky right-0 bg-background z-10 px-4 py-3" 
                              style={{ width: `${actionsColumnWidth}px`, minWidth: `${actionsColumnWidth}px` }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {renderActions(item)}
                            </TableCell>
                          )}
                        </TableRow>
                      )
                    })
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {onPageChange && displayTotalPages > 1 && (
              <div className="border-t p-4">
                <TablePagination
                  currentPage={currentPage || 1}
                  totalPages={displayTotalPages}
                  itemsPerPage={itemsPerPage || 15}
                  totalItems={sortedData.length}
                  onPageChange={onPageChange}
                  onItemsPerPageChange={onItemsPerPageChange}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* View Management Sidebar */}
      {onCreateView && (
        <Sheet open={showViewSidebar} onOpenChange={setShowViewSidebar}>
          <SheetContent side="left" className="w-80 p-0">
            <div className="flex flex-col h-full">
              <div className="flex items-center justify-between px-4 py-3 border-b">
                <SheetTitle className="text-base font-semibold">Create view</SheetTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowViewSidebar(false)}
                  className="h-8 w-8 p-0"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex-1 p-4 space-y-6">
                <div className="space-y-2">
                  <div className="relative">
                    <List className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input placeholder="All Items" className="pl-10" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm text-muted-foreground">View type</Label>
                  <Select defaultValue="table">
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="table">Table</SelectItem>
                      <SelectItem value="kanban">Kanban</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button className="w-full" onClick={onCreateView}>
                  Create
                </Button>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      )}

      {/* Filter Dialog */}
      {renderFilters && (
        <Sheet open={showFilterDialog} onOpenChange={onFilterDialogChange}>
          <SheetContent side="right" className="w-96">
            <SheetHeader>
              <SheetTitle>Filters</SheetTitle>
              <SheetDescription>
                Add filters to narrow down your results
              </SheetDescription>
            </SheetHeader>
            <div className="mt-6">
              {renderFilters()}
            </div>
          </SheetContent>
        </Sheet>
      )}

      {/* Fields Management Sidebar */}
      <Sheet open={showFieldsSidebar} onOpenChange={setShowFieldsSidebar}>
        <SheetContent side="right" className="w-80 p-0">
          <DragDropContext onDragEnd={handleDragEnd}>
            <div className="flex flex-col h-full">
              <div className="flex items-center gap-2 px-4 py-3 border-b">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowFieldsSidebar(false)}
                  className="h-8 w-8 p-0"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <SheetTitle className="text-base font-semibold">Fields</SheetTitle>
              </div>
              <ScrollArea className="flex-1">
                <div className="p-2">
                  <Droppable droppableId="visible-fields">
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className={`space-y-1 ${snapshot.isDraggingOver ? 'bg-muted/50 rounded-lg' : ''}`}
                      >
                        {columns
                          .filter(col => col.visible)
                          .sort((a, b) => a.order - b.order)
                          .map((column, index) => (
                            <Draggable key={column.id} draggableId={column.id} index={index}>
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  className={`flex items-center gap-2 p-2 rounded-md hover:bg-muted/50 transition-colors ${
                                    snapshot.isDragging ? 'bg-muted shadow-lg' : ''
                                  }`}
                                >
                                  <div {...provided.dragHandleProps} className="cursor-grab active:cursor-grabbing">
                                    <GripVertical className="h-4 w-4 text-muted-foreground" />
                                  </div>
                                  <div className="flex items-center gap-2 flex-1 min-w-0">
                                    {column.icon}
                                    <span className="text-sm font-medium truncate">{column.label}</span>
                                  </div>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 w-6 p-0"
                                    onClick={() => handleToggleColumnVisibility(column.id)}
                                  >
                                    <Eye className="h-4 w-4 text-muted-foreground" />
                                  </Button>
                                </div>
                              )}
                            </Draggable>
                          ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>

                  <div className="mt-4 pt-4 border-t">
                    <div className="flex items-center gap-2 px-2 mb-2">
                      <EyeOff className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-medium text-muted-foreground">Hidden Fields</span>
                      <ChevronRight className="h-4 w-4 text-muted-foreground ml-auto" />
                    </div>
                    <Droppable droppableId="hidden-fields">
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          className={`space-y-1 ${snapshot.isDraggingOver ? 'bg-muted/50 rounded-lg' : ''}`}
                        >
                          {columns
                            .filter(col => !col.visible)
                            .sort((a, b) => a.order - b.order)
                            .map((column, index) => (
                              <Draggable key={column.id} draggableId={column.id} index={index}>
                                {(provided, snapshot) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    className={`flex items-center gap-2 p-2 rounded-md hover:bg-muted/50 transition-colors ${
                                      snapshot.isDragging ? 'bg-muted shadow-lg' : ''
                                    }`}
                                  >
                                    <div {...provided.dragHandleProps} className="cursor-grab active:cursor-grabbing">
                                      <GripVertical className="h-4 w-4 text-muted-foreground" />
                                    </div>
                                    <div className="flex items-center gap-2 flex-1 min-w-0">
                                      {column.icon}
                                      <span className="text-sm text-muted-foreground truncate">{column.label}</span>
                                    </div>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-6 w-6 p-0"
                                      onClick={() => handleToggleColumnVisibility(column.id)}
                                    >
                                      <EyeOff className="h-4 w-4 text-muted-foreground" />
                                    </Button>
                                  </div>
                                )}
                              </Draggable>
                            ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </div>
                </div>
              </ScrollArea>
            </div>
          </DragDropContext>
        </SheetContent>
      </Sheet>
    </div>
  )
}
